import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Search, Landmark, MapPin, Trash2, Edit3, X, Download, Merge, FileSpreadsheet, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { LocationPickerModal } from '../LocationPickerModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VENUE_TYPES = [
  ['teatro', 'Teatro'],
  ['sala', 'Sala / Auditorio'],
  ['bar', 'Bar / Pub'],
  ['restaurante', 'Restaurante'],
  ['centro_cultural', 'Centro cultural'],
  ['estadio', 'Estadio'],
  ['parque', 'Parque / Plaza'],
  ['museo', 'Museo'],
  ['otro', 'Otro'],
];

const emptyForm = {
  name: '',
  address: '',
  latitude: null,
  longitude: null,
  commune_id: '',
  venue_type: '',
  notes: '',
  is_active: true,
};

export default function VenuesTab() {
  const [items, setItems] = useState([]);
  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [communeFilter, setCommuneFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pendingRegionId, setPendingRegionId] = useState('');
  const [mapOpen, setMapOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkFileRef = React.useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/venues/template.xlsx`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plancity-lugares-plantilla.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo descargar la plantilla');
    }
  };

  const handleBulkUploadChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset input so re-selecting the same file re-fires
    if (!file) return;
    setBulkUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/venues/bulk-upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(res.data);
      const created = res.data?.created?.length || 0;
      const skipped = res.data?.skipped_duplicate?.length || 0;
      const errored = res.data?.errors?.length || 0;
      if (created > 0) toast.success(`${created} lugar${created === 1 ? '' : 'es'} creado${created === 1 ? '' : 's'}${skipped ? ` · ${skipped} duplicado${skipped === 1 ? '' : 's'} omitido${skipped === 1 ? '' : 's'}` : ''}${errored ? ` · ${errored} con error` : ''}`);
      else if (skipped > 0 && errored === 0) toast.info(`Todos los lugares del archivo (${skipped}) ya existían`);
      else if (errored > 0) toast.error(`Nada creado · ${errored} filas con error`);
      // Reload the list so the new venues appear immediately.
      fetchAll();
    } catch (err) {
      toast.error('Error en la carga masiva', { description: err.response?.data?.detail || String(err) });
    } finally {
      setBulkUploading(false);
    }
  };

  const communesFilteredForForm = useMemo(() => {
    if (!pendingRegionId && !form.commune_id) return communes;
    const currentRegion = pendingRegionId || (() => {
      const c = communes.find((c) => c.id === form.commune_id);
      return c?.region_id || '';
    })();
    if (!currentRegion) return communes;
    return communes.filter((c) => c.region_id === currentRegion);
  }, [communes, pendingRegionId, form.commune_id]);

  const selectedRegionId = useMemo(() => {
    if (form.commune_id) {
      const c = communes.find((c) => c.id === form.commune_id);
      if (c?.region_id) return c.region_id;
    }
    return pendingRegionId || '';
  }, [form.commune_id, communes, pendingRegionId]);

  const communesFilteredForListView = useMemo(() => {
    if (regionFilter === 'all') return communes;
    return communes.filter((c) => c.region_id === regionFilter);
  }, [communes, regionFilter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (regionFilter && regionFilter !== 'all') params.set('region_id', regionFilter);
      if (communeFilter && communeFilter !== 'all') params.set('commune_id', communeFilter);
      if (showInactive) params.set('include_inactive', 'true');
      params.set('limit', '500');
      const [vRes, rRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/venues?${params.toString()}`),
        axios.get(`${API_URL}/api/admin/regions`),
        axios.get(`${API_URL}/api/admin/communes`),
      ]);
      setItems(vRes.data || []);
      setRegions(rRes.data || []);
      setCommunes(cRes.data || []);
    } catch (e) {
      toast.error('No se pudieron cargar los lugares');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  // Auto-refetch when filters change
  useEffect(() => {
    const t = setTimeout(fetchAll, 300);
    return () => clearTimeout(t);
  }, [q, regionFilter, communeFilter, showInactive]);

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setPendingRegionId('');
    setDialogOpen(true);
  };

  const openEdit = (v) => {
    setForm({
      name: v.name || '',
      address: v.address || '',
      latitude: v.latitude ?? null,
      longitude: v.longitude ?? null,
      commune_id: v.commune_id || '',
      venue_type: v.venue_type || '',
      notes: v.notes || '',
      is_active: v.is_active !== false,
    });
    setEditingId(v.id);
    setPendingRegionId(v.region_id || '');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setPendingRegionId('');
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Nombre requerido');
    if (!form.commune_id) return toast.error('Comuna requerida');
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim() };
      if (editingId) {
        const res = await axios.put(`${API_URL}/api/admin/venues/${editingId}`, payload);
        setItems((prev) => prev.map((v) => (v.id === editingId ? res.data : v)));
        toast.success('Lugar actualizado');
      } else {
        const res = await axios.post(`${API_URL}/api/admin/venues`, payload);
        setItems((prev) => [res.data, ...prev]);
        toast.success('Lugar creado');
      }
      closeDialog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/admin/venues/${deleteTarget.id}`);
      setItems((prev) => prev.filter((v) => v.id !== deleteTarget.id));
      toast.success('Lugar eliminado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleteTarget(null);
    }
  };

  const runBackfill = async () => {
    if (communeFilter === 'all') return toast.error('Elige una comuna primero para importar sus eventos');
    if (!window.confirm('Se extraerán lugares únicos desde los eventos existentes de la comuna seleccionada. Duplicados con lugares ya creados serán ignorados. ¿Continuar?')) return;
    setBackfilling(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/venues/backfill-from-events?commune_id=${communeFilter}`);
      const d = res.data || {};
      toast.success(`Importados ${d.inserted} lugares · ${d.skipped_duplicate} duplicados omitidos (de ${d.scanned_events} eventos)`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al importar');
    } finally {
      setBackfilling(false);
    }
  };

  const runDedupe = async () => {
    const scope = communeFilter !== 'all' ? `?commune_id=${communeFilter}` :
      regionFilter !== 'all' ? `?region_id=${regionFilter}` : '';
    if (!scope) return toast.error('Elige una región o comuna primero');
    if (!window.confirm('Se fusionarán lugares duplicados (mismo nombre normalizado o mismas coordenadas). Se conservará el más antiguo. ¿Continuar?')) return;
    setDeduping(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/venues/dedupe${scope}`);
      const d = res.data || {};
      toast.success(`Deduplicación lista · ${d.kept} conservados · ${d.merged} fusionados`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al deduplicar');
    } finally {
      setDeduping(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = items.length > 0 && items.every((v) => selectedIds.has(v.id));

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        items.forEach((v) => next.delete(v.id));
        return next;
      }
      const next = new Set(prev);
      items.forEach((v) => next.add(v.id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const runBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/venues/bulk-delete`, { ids });
      const deleted = res.data?.deleted || 0;
      setItems((prev) => prev.filter((v) => !selectedIds.has(v.id)));
      clearSelection();
      toast.success(`${deleted} lugar(es) eliminados`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar en lote');
    } finally {
      setBulkDeleting(false);
      setBulkDeleteOpen(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
              <Landmark className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#064E3B]">Lugares</h2>
              <p className="text-sm text-gray-600 max-w-md">Catálogo de recintos y locales. Al elegir un lugar al crear un evento, se autocompleta la dirección, coordenadas y ubicación en el mapa.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={bulkFileRef}
              type="file"
              accept=".xlsx,.xlsm"
              onChange={handleBulkUploadChange}
              className="hidden"
              data-testid="venue-bulk-file-input"
            />
            <Button
              variant="outline"
              onClick={downloadTemplate}
              title="Descarga la plantilla .xlsx para llenar y volver a subir"
              data-testid="venue-download-template-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              Plantilla
            </Button>
            <Button
              variant="outline"
              onClick={() => bulkFileRef.current?.click()}
              disabled={bulkUploading}
              title="Sube un .xlsx con la plantilla llena para crear varios lugares de una vez"
              data-testid="venue-bulk-upload-btn"
            >
              {bulkUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Carga masiva
            </Button>
            <Button
              variant="outline"
              onClick={runBackfill}
              disabled={backfilling || communeFilter === 'all'}
              title={communeFilter === 'all' ? 'Filtra por comuna para habilitar' : 'Importar lugares únicos desde eventos existentes'}
              data-testid="venue-backfill-btn"
            >
              {backfilling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Importar desde eventos
            </Button>
            <Button
              variant="outline"
              onClick={runDedupe}
              disabled={deduping || (communeFilter === 'all' && regionFilter === 'all')}
              title={communeFilter === 'all' && regionFilter === 'all' ? 'Filtra por región/comuna para habilitar' : 'Fusionar lugares duplicados'}
              data-testid="venue-dedupe-btn"
            >
              {deduping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Merge className="w-4 h-4 mr-1" />}
              Deduplicar
            </Button>
            <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="venue-create-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuevo lugar
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o dirección…"
            className="pl-9"
            data-testid="venue-search"
          />
        </div>
        <Select value={regionFilter} onValueChange={(v) => { setRegionFilter(v); setCommuneFilter('all'); }}>
          <SelectTrigger className="w-[220px]" data-testid="venue-region-filter"><SelectValue placeholder="Región" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Todas las regiones</SelectItem>
            {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={communeFilter} onValueChange={setCommuneFilter}>
          <SelectTrigger className="w-[220px]" data-testid="venue-commune-filter"><SelectValue placeholder="Comuna" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Todas las comunas</SelectItem>
            {communesFilteredForListView.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-gray-600 ml-2">
          <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="venue-show-inactive" />
          Ver inactivos
        </label>
      </div>

      {/* Selection bar (shown when items are checked) */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 flex-wrap rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5" data-testid="venue-selection-bar">
          <span className="text-sm font-medium text-emerald-900">
            {selectedIds.size} seleccionado{selectedIds.size === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={toggleSelectAllVisible}
            className="text-xs text-emerald-700 underline hover:no-underline"
            data-testid="venue-select-all-visible"
          >
            {allVisibleSelected ? 'Deseleccionar visibles' : `Seleccionar todos los ${items.length} visibles`}
          </button>
          <button
            type="button"
            onClick={clearSelection}
            className="text-xs text-gray-600 underline hover:no-underline"
            data-testid="venue-clear-selection"
          >
            Limpiar selección
          </button>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setBulkDeleteOpen(true)}
              disabled={bulkDeleting}
              data-testid="venue-bulk-delete-btn"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Eliminar seleccionados
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-gray-400 border border-dashed rounded-2xl">
          Sin lugares que coincidan. Crea uno para empezar.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((v) => {
            const isSelected = selectedIds.has(v.id);
            return (
            <div
              key={v.id}
              data-testid={`venue-item-${v.id}`}
              className={`relative rounded-2xl bg-white border p-4 pl-10 hover:shadow-md transition ${isSelected ? 'border-emerald-500 ring-2 ring-emerald-200' : ''}`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleSelect(v.id)}
                className="absolute top-4 left-3 w-4 h-4 accent-emerald-600 cursor-pointer"
                data-testid={`venue-select-${v.id}`}
                aria-label={`Seleccionar ${v.name}`}
              />
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h4 className="font-semibold text-[#064E3B] truncate">{v.name}</h4>
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                    <MapPin className="w-3 h-3 shrink-0" />
                    <span className="truncate">{v.commune_name}{v.region_name && v.region_name !== v.commune_name ? `, ${v.region_name}` : ''}</span>
                  </p>
                </div>
                {!v.is_active && <Badge variant="outline" className="text-[10px]">Inactivo</Badge>}
              </div>
              {v.address && <p className="text-xs text-gray-600 mt-2 line-clamp-2">{v.address}</p>}
              {(v.latitude != null && v.longitude != null) && (
                <p className="text-[11px] text-gray-400 mt-1">📍 {v.latitude.toFixed(5)}, {v.longitude.toFixed(5)}</p>
              )}
              {v.venue_type && (
                <span className="inline-block mt-2 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">
                  {VENUE_TYPES.find(([k]) => k === v.venue_type)?.[1] || v.venue_type}
                </span>
              )}
              <div className="flex items-center gap-1 mt-3 pt-3 border-t">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openEdit(v)} data-testid={`venue-edit-${v.id}`}>
                  <Edit3 className="w-3 h-3 mr-1" /> Editar
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={() => setDeleteTarget(v)} data-testid={`venue-delete-${v.id}`}>
                  <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                </Button>
              </div>
            </div>
          );})}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) closeDialog(); }}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] p-0 flex flex-col gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
            <DialogTitle>{editingId ? 'Editar lugar' : 'Nuevo lugar'}</DialogTitle>
            <DialogDescription>Los lugares son reutilizables. Al elegir uno al crear un evento, se autocompleta dirección, coordenadas y comuna.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
            <div>
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Teatro Sidarte" data-testid="venue-form-name" />
            </div>

            <div className="rounded-xl border border-gray-200 p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1 mb-2"><MapPin className="w-3 h-3" /> Ubicación</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Región *</Label>
                  <Select
                    value={selectedRegionId || undefined}
                    onValueChange={(regionId) => {
                      setPendingRegionId(regionId);
                      const cur = communes.find((c) => c.id === form.commune_id);
                      if (cur && cur.region_id !== regionId) {
                        setForm({ ...form, commune_id: '' });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="venue-form-region"><SelectValue placeholder="Elige región" /></SelectTrigger>
                    <SelectContent className="max-h-72">{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Comuna *</Label>
                  <Select value={form.commune_id || undefined} onValueChange={(v) => setForm({ ...form, commune_id: v })}>
                    <SelectTrigger data-testid="venue-form-commune"><SelectValue placeholder="Elige comuna" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {communesFilteredForForm.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3">
                <Label>Dirección</Label>
                <div className="flex gap-2">
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Av Providencia 1234" className="flex-1" data-testid="venue-form-address" />
                  <Button type="button" variant="outline" onClick={() => setMapOpen(true)} className="whitespace-nowrap" data-testid="venue-form-map-btn">
                    <MapPin className="w-4 h-4 mr-1" /> Buscar en mapa
                  </Button>
                </div>
                {(form.latitude != null && form.longitude != null) && (
                  <p className="text-[11px] text-gray-500 mt-1">📍 {Number(form.latitude).toFixed(5)}, {Number(form.longitude).toFixed(5)}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Tipo de lugar</Label>
                <Input
                  list="venue-types-list"
                  value={form.venue_type || ''}
                  onChange={(e) => setForm({ ...form, venue_type: e.target.value })}
                  placeholder="Elige o crea uno nuevo (ej: Teatro, Iglesia, Cancha…)"
                  data-testid="venue-form-type"
                />
                <datalist id="venue-types-list">
                  {(() => {
                    const seen = new Set();
                    const opts = [];
                    // Include defaults (label form)
                    VENUE_TYPES.forEach(([, label]) => {
                      const k = label.toLowerCase();
                      if (!seen.has(k)) { seen.add(k); opts.push(label); }
                    });
                    // Include any custom types already stored in the DB
                    items.forEach((v) => {
                      const t = (v.venue_type || '').trim();
                      if (t && !seen.has(t.toLowerCase())) { seen.add(t.toLowerCase()); opts.push(t); }
                    });
                    return opts.map((label) => <option key={label} value={label} />);
                  })()}
                </datalist>
                <p className="text-[11px] text-gray-500 mt-1">Escribe libremente para crear un tipo nuevo. Se sumará a la lista al guardar.</p>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="venue-form-active" />
                  Activo (visible en formularios)
                </label>
              </div>
            </div>

            <div>
              <Label>Notas internas (opcional)</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Contacto, aforo, accesos, restricciones…" data-testid="venue-form-notes" />
            </div>
          </div>
          <div className="border-t bg-white px-6 py-3 flex justify-end gap-2 shrink-0">
            <Button variant="outline" onClick={closeDialog} data-testid="venue-form-cancel">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="venue-form-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Map picker */}
      <LocationPickerModal
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        initialAddress={form.address}
        initialLat={form.latitude ? parseFloat(form.latitude) : null}
        initialLng={form.longitude ? parseFloat(form.longitude) : null}
        initialCommuneId={form.commune_id || ''}
        communes={communes}
        onConfirm={(lat, lng, address) => setForm((prev) => ({ ...prev, latitude: lat, longitude: lng, ...(address ? { address } : {}) }))}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar lugar</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que quieres eliminar <b>{deleteTarget?.name}</b>? Los eventos previamente creados con este lugar mantendrán su dirección y coordenadas, pero ya no podrás seleccionarlo en formularios nuevos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="venue-confirm-delete">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Bulk delete confirm */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar {selectedIds.size} lugar{selectedIds.size === 1 ? '' : 'es'}</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Los eventos previamente creados con estos lugares mantendrán su dirección y coordenadas, pero ya no podrás seleccionarlos en formularios nuevos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={runBulkDelete} className="bg-red-600 hover:bg-red-700" data-testid="venue-confirm-bulk-delete">
              Eliminar {selectedIds.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk upload result summary */}
      <Dialog open={!!bulkResult} onOpenChange={(o) => !o && setBulkResult(null)}>
        <DialogContent className="max-w-2xl" data-testid="venue-bulk-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              Resultado de carga masiva
            </DialogTitle>
          </DialogHeader>
          {bulkResult && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{bulkResult.created?.length || 0}</div>
                  <div className="text-[11px] uppercase tracking-wide text-emerald-700">Creados</div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">{bulkResult.skipped_duplicate?.length || 0}</div>
                  <div className="text-[11px] uppercase tracking-wide text-amber-700">Ya existentes</div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                  <div className="text-2xl font-bold text-red-700">{bulkResult.errors?.length || 0}</div>
                  <div className="text-[11px] uppercase tracking-wide text-red-700">Con error</div>
                </div>
              </div>
              {(bulkResult.errors?.length || 0) > 0 && (
                <div>
                  <div className="text-xs font-semibold text-red-900 uppercase tracking-wide mb-1">Errores</div>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-red-200 divide-y divide-red-100">
                    {bulkResult.errors.map((e) => (
                      <div key={`err-${e.row}`} className="px-3 py-2 bg-red-50/50 text-xs text-red-900">
                        <b>Fila {e.row}:</b> {e.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(bulkResult.skipped_duplicate?.length || 0) > 0 && (
                <div>
                  <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-1">Duplicados (no se crearon)</div>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-amber-200 divide-y divide-amber-100">
                    {bulkResult.skipped_duplicate.map((s) => (
                      <div key={`sk-${s.row}`} className="px-3 py-2 bg-amber-50/40 text-xs text-amber-900">
                        <b>Fila {s.row}:</b> {s.name} · {s.commune}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(bulkResult.created?.length || 0) > 0 && (
                <div>
                  <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wide mb-1">Creados</div>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-emerald-200 divide-y divide-emerald-100">
                    {bulkResult.created.map((c) => (
                      <div key={`cr-${c.row}`} className="px-3 py-2 bg-emerald-50/40 text-xs text-emerald-900">
                        <b>Fila {c.row}:</b> {c.name} · {c.commune}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end mt-4">
            <Button onClick={() => setBulkResult(null)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="venue-bulk-result-close">
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
